import { notificationsClient } from 'clients/NotificationsClient/NotificationsClient';
import { searchClient } from 'clients/SearchClient/SearchClient';
import { getContactSummaryInfos } from 'services/browser-extension/indeed/getContactSummary';
import { getConversationsIdBasedOnJobSeeker } from 'services/browser-extension/indeed/getConversation';
import { getJobSeekerByMongoOrIndeed } from 'services/browser-extension/indeed/getJobSeeker';
import { buildMessagesBasedOnConversationsId } from 'services/browser-extension/indeed/getMessages';
import {
  PullIndeedMessageReturnType,
  SyncStatus,
  UsePullIndeedMessageBasedOnCandidateOptions,
} from 'services/browser-extension/indeed/indeed.types';
import { getIsFeatureToggleEnabled } from '@/features/feature-toggle/lib';
import { CampaignJobboard } from 'shared/legacy/contexts/SelectedCampaignContext/SelectedCampaignContext.types';
import { MessageReceivedPlatform } from 'shared/legacy/hooks/useBrowserExtension/platform.types';
import { useCurrentUserQuery } from 'shared/legacy/hooks/useCurrentUserQuery';

const syncInitialState = {
  syncedStatus: SyncStatus.Pending,
  syncedAt: null,
  hasMessages: false,
};

export const usePullIndeedMessageBasedOnCandidate = ({
  candidate,
  indeedConfig,
}: UsePullIndeedMessageBasedOnCandidateOptions) => {
  const { data: currentUser } = useCurrentUserQuery();

  const pullIndeedMessages: PullIndeedMessageReturnType = async () => {
    if (getIsFeatureToggleEnabled((t) => t.INDEED_POLLING_ENABLED)) return syncInitialState;
    if (candidate?.activeJobBoard !== CampaignJobboard.Indeed) return syncInitialState;
    if (
      !candidate?.es_person_id ||
      !candidate?.name ||
      !currentUser?.tenant.id ||
      !candidate?.es_person_id_without_platform
    )
      return syncInitialState;

    try {
      const { contactHistoryList, candidateInformation } = await getContactSummaryInfos({
        indeedConfig,
        candidateId: candidate?.es_person_id_without_platform,
      });

      newrelic.addPageAction('IndeedSyncStep', {
        step: 'contact_summary',
        tenantId: currentUser?.tenant.id,
        candidateId: candidate?.es_person_id_without_platform,
        hasContactHistory: String(!!contactHistoryList),
        contactHistoryCount: contactHistoryList?.length ?? 0,
      });

      if (!contactHistoryList || contactHistoryList.length == 0) {
        const syncedAt = await fetchAndSetLastMessage();
        return {
          syncedStatus: SyncStatus.Synced,
          syncedAt: syncedAt || null,
          hasMessages: false,
        };
      }

      if (contactHistoryList.length > 0) {
        if (candidateInformation.email) {
          await searchClient.createContactInfo({
            phoneNumber: candidateInformation.phoneNumber,
            email: candidateInformation.email,
            esPersonId: candidate?.es_person_id,
          });
        }

        const jobSeekerKey = await getJobSeekerByMongoOrIndeed({
          tenantId: currentUser?.tenant.id,
          candidateId: candidate?.es_person_id_without_platform,
          candidateName: candidate?.name,
          indeedConfig: indeedConfig,
          userEmail: currentUser?.email,
        });

        newrelic.addPageAction('IndeedSyncStep', {
          step: 'job_seeker_key',
          tenantId: currentUser?.tenant.id,
          candidateId: candidate?.es_person_id_without_platform,
          jobSeekerKeyFound: String(!!jobSeekerKey),
          jobSeekerKey: jobSeekerKey ?? '',
        });

        if (!jobSeekerKey && indeedConfig.email) {
          if (!currentUser?.email || !candidate?.es_person_id)
            return {
              syncedStatus: SyncStatus.Error,
              syncedAt: null,
              hasMessages: false,
            };
          const lastMessage = await notificationsClient.fetchMostRecentSyncedIndeedMessage({
            credentialUsername: currentUser.email,
            candidateId: candidate?.es_person_id,
            platform: MessageReceivedPlatform.INDEED,
          });

          if (lastMessage?.data.most_recent_message) {
            return {
              syncedStatus: SyncStatus.Synced,
              syncedAt: new Date(lastMessage.data.most_recent_message.sentAt),
              hasMessages: false,
            };
          }
          return {
            syncedStatus: SyncStatus.Error,
            syncedAt: null,
            hasMessages: false,
          };
        }

        const conversationsId = await getConversationsIdBasedOnJobSeeker({
          indeedConfig,
          jobSeekerKey: jobSeekerKey as string,
          tenantId: currentUser?.tenant.id,
          candidateId: candidate?.es_person_id_without_platform,
        });

        newrelic.addPageAction('IndeedSyncStep', {
          step: 'conversations',
          tenantId: currentUser?.tenant.id,
          candidateId: candidate?.es_person_id_without_platform,
          conversationCount: conversationsId.length,
          conversationIds: JSON.stringify(conversationsId),
        });

        if (conversationsId.length === 0) {
          const syncedAt = await fetchAndSetLastMessage();
          return {
            syncedStatus: SyncStatus.Error,
            syncedAt: syncedAt || null,
            hasMessages: false,
          };
        }

        const indeedMessages = await buildMessagesBasedOnConversationsId({
          indeedConfig: indeedConfig,
          conversationsId: conversationsId,
          licenseEmailAddress: indeedConfig.email,
          tenantId: currentUser?.tenant.id,
          candidateId: candidate?.es_person_id_without_platform,
          candidateName: candidate?.name,
          userEmail: currentUser?.email,
        });

        newrelic.addPageAction('IndeedSyncStep', {
          step: 'messages',
          tenantId: currentUser?.tenant.id,
          candidateId: candidate?.es_person_id_without_platform,
          messageCount: indeedMessages.length,
          conversationCount: conversationsId.length,
        });

        if (indeedMessages.length > 0) {
          const syncedAt = await fetchAndSetLastMessage();
          return {
            syncedStatus: SyncStatus.Synced,
            syncedAt: syncedAt || null,
            hasMessages: true,
          };
        }

        if (indeedMessages.length === 0) {
          const syncedAt = await fetchAndSetLastMessage();
          return {
            syncedStatus: SyncStatus.Synced,
            syncedAt: syncedAt || null,
            hasMessages: false,
          };
        }
      }
      return {
        syncedStatus: SyncStatus.Synced,
        syncedAt: null,
        hasMessages: false,
      };
    } catch (error) {
      console.error(error);
      return {
        syncedStatus: SyncStatus.Error,
        syncedAt: null,
        hasMessages: false,
      };
    }
  };

  const fetchAndSetLastMessage = async () => {
    if (!currentUser?.email || !candidate?.es_person_id_without_platform) {
      return;
    }

    const lastMessage = await notificationsClient.fetchMostRecentSyncedIndeedMessage({
      credentialUsername: currentUser.email,
      candidateId: candidate?.es_person_id_without_platform,
      platform: MessageReceivedPlatform.INDEED,
    });

    if (lastMessage?.data.most_recent_message) {
      return new Date(lastMessage.data.most_recent_message.sentAt);
    }
  };

  return { pullIndeedMessages };
};
